<script setup lang="ts">
import type { HighlightedRegionPropertiesSectionProps } from '.';
import type Carousel from '@/components/ui/carousel/Carousel.vue';

import { Container } from '@/components/Container';
import { Button } from '@/components/ui/button';
import { CarouselGenericAction } from '@/components/ui/carousel/CarouselGenericAction';
import { CarouselItemSelector } from '@/components/ui/carousel/CarouselItemSelector';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { dehyphenateString } from '~~/utils';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

const props = withDefaults(
	defineProps<HighlightedRegionPropertiesSectionProps>(),
	{
		buttonText: 'Ver todos'
	}
);

const title = computed<string>(() => {
	if (props.title?.trim()) return props.title;

	return `Imóveis em destaque em ${props.region}`;
});

const SLIDE_DELAY = 2000;

const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null);

const carouselItemSelectorContainerRef = ref<HTMLDivElement | null>(null);

const hyphenatedRegion = computed<string>(() =>
	dehyphenateString(props.region)
);

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef,
	carouselItemSelectorContainerRef,
	cyclic: true
});
</script>
<template>
	<section class="w-full bg-white">
		<Container class="px-0 py-8 md:py-[60px]">
			<header class="flex flex-col items-start gap-4 pb-6 md:pb-9">
				<Skeleton
					:is-active="isLoading"
					variant="white"
					class="px-5"
					:class="{ 'h-[34px] w-1/2 md:w-[359px]': isLoading }"
				>
					<h2
						class="flex items-center font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-primary md:text-4xl md:tracking-[-0.045rem]"
					>
						{{ title }}
					</h2>
				</Skeleton>
			</header>

			<div class="flex w-full flex-col gap-6 md:gap-9">
				<div v-if="isLoading" class="flex items-center gap-5 px-5">
					<PropertyCardSkeleton
						v-for="index in 2"
						:key="`highlighted-property-skeleton-${index}`"
						variant="whiteBackground"
					/>
				</div>

				<Carousel
					v-else
					ref="carouselContainerRef"
					class="w-full"
					:plugins="[WheelGesturesPlugin()]"
					:opts="{ align: 'center' }"
				>
					<CarouselContent class="gap-6">
						<CarouselItem
							v-for="property in properties"
							:key="property.commercialId"
							class="max-h-[345px] max-w-[335px] md:max-h-[380px] md:max-w-[464px]"
							:class="{
								'pl-6': currentCarouselItemIndex === 0,
								'pr-6': currentCarouselItemIndex === properties.length - 1
							}"
						>
							<PropertyCard
								variant="whiteBackground"
								:property="property"
								:section="section"
							/>
						</CarouselItem>
					</CarouselContent>
				</Carousel>
				<div
					class="flex h-auto w-full flex-col-reverse items-start justify-between gap-6 px-6 md:mx-0 md:flex-row md:items-center"
				>
					<NuxtLink
						v-slot="{ href, navigate }"
						custom
						:to="
							PropertyFiltersPresenter.getPathFromFilters({
								states: [props.state],
								cities: [props.city],
								regions: [hyphenatedRegion]
							})
						"
					>
						<Button
							as="a"
							variant="primary"
							class="w-min cursor-pointer self-start px-5 py-3 md:px-5 md:py-[11px]"
							:href="href"
							:is-skeleton-loading="isLoading"
							skeleton-classes="w-[231px] md:w-[290px] h-[52px]"
							skeleton-variant="white"
							@click="navigate"
						>
							{{ buttonText }}
							<IconMaterialSymbolsNorthEast class="w-4 md:w-5" />
						</Button>
					</NuxtLink>

					<Skeleton
						:is-active="isLoading"
						variant="white"
						:class="{ 'h-7 w-1/2 md:w-[359px]': isLoading }"
					>
						<div
							class="flex w-full items-center justify-end gap-3 md:w-auto md:justify-center md:gap-5"
						>
							<div
								ref="carouselItemSelectorContainerRef"
								class="flex w-full justify-end gap-2 md:gap-3"
							>
								<CarouselItemSelector
									v-for="(_, index) in scrollableSlidesNumber"
									:key="`selector-${index}`"
									:active="currentCarouselItemIndex === index"
									:delay="SLIDE_DELAY"
									:dynamic-width="carouselDynamicItemWidth"
									variant="dark"
									@click="scrollTo(index, false)"
								/>
							</div>
							<div class="flex gap-1 md:gap-2">
								<CarouselGenericAction
									variant="dark"
									@click="scrollTo(currentCarouselItemIndex - 1)"
								>
									<IconMaterialSymbolsLightChevronLeft
										width="28px"
										height="28px"
									/>
								</CarouselGenericAction>
								<CarouselGenericAction variant="dark">
									<IconMaterialSymbolsLightChevronRight
										width="28px"
										height="28px"
										@click="scrollTo(currentCarouselItemIndex + 1)"
									/>
								</CarouselGenericAction>
							</div>
						</div>
					</Skeleton>
				</div>
			</div>
		</Container>
	</section>
</template>
